import {
  ApartmentOutlined,
  AuditOutlined,
  BarChartOutlined,
  BrushOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ControlOutlined,
  CustomerServiceOutlined,
  EditOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FontSizeOutlined,
  FundOutlined,
  HistoryOutlined,
  IdcardOutlined,
  LineChartOutlined,
  DesktopOutlined,
  LockOutlined,
  NewspaperOutlined,
  PictureOutlined,
  PlaySquareOutlined,
  ProfileOutlined,
  ProjectOutlined,
  RadarChartOutlined,
  SearchOutlined,
  SettingOutlined,
  SlidersOutlined,
  SoundOutlined,
  TeamOutlined,
  YoutubeOutlined,
} from '@/app/icons';
import type { EffectivePermissions } from '@halo/shared';
import type { ReactNode } from 'react';
import type { AccentHue } from './theme-tokens';

export interface NavigationItem {
  /** Đường dẫn trong ứng dụng, vd. `/seo`. */
  path: string;
  label: string;
  icon: ReactNode;
  /** Vắng mặt = ai đăng nhập cũng thấy. Máy chủ vẫn chặn thật; đây chỉ để menu gọn. */
  visible?: (permissions: EffectivePermissions) => boolean;
  /** Mở ở thẻ mới (dịch vụ chạy ngoài ứng dụng). */
  external?: boolean;
  /** Tông màu của mục RỜI (không nằm trong nhóm nào, xem `DAILY`); mục trong nhóm theo màu của nhóm. */
  hue?: AccentHue;
}

export interface NavigationGroup {
  key: string;
  label: string;
  /** Hiện khi thanh bên THU GỌN (máy tính bảng): không có icon thì Ant Design chỉ vẽ chữ cái đầu. */
  icon: ReactNode;
  /** Tông màu của cả nhóm — ô icon trong menu, tiêu đề màn, thẻ tổng quan (xem `theme-tokens.ts`). */
  hue: AccentHue;
  items: NavigationItem[];
}

/** Màn đầu tiên: BẢNG TIN (03/10/2026; «Việc cần xử lý» 23/09–03/10/2026 đã bỏ, trước nữa là «Tổng quan»). */
export const HOME: NavigationItem = {
  path: '/',
  label: 'Bảng tin',
  icon: <NewspaperOutlined />,
};
const HOME_HUE: AccentHue = 'teal';

/**
 * VIỆC HẰNG NGÀY — bốn mục rời ngay dưới Bảng tin, đúng thứ tự một ngày làm việc (chủ dự án, 05/10/2026: «sau
 * bảng tin là chấm công … sau bảng chấm công là báo cáo công việc hằng ngày … sau bảng báo cáo công việc là nhập
 * doanh thu hằng ngày … sau bảng nhập doanh thu là Phiếu đề xuất và đơn»). Không gập được như nhóm; mỗi mục
 * giữ tông màu của nhóm nó từng nằm.
 */
export const DAILY: NavigationItem[] = [
  { path: '/attendance', label: 'Chấm công', icon: <ClockCircleOutlined />, hue: 'violet' },
  {
    path: '/work-reports',
    label: 'Báo cáo công việc cá nhân',
    icon: <FileTextOutlined />,
    hue: 'violet',
  },
  {
    path: '/revenue/entry',
    label: 'Nhập doanh thu',
    icon: <EditOutlined />,
    hue: 'amber',
    visible: (p) => p.canAccessRevenue,
  },
  { path: '/requests', label: 'Phiếu đề xuất & đơn', icon: <AuditOutlined />, hue: 'violet' },
];

/**
 * MỤC LỤC — 6 nhóm gập/mở được (tổ chức lại 20/09/2026). Phần tiền tách thành ba màn ĐỌC
 * (báo cáo ngày/tuần/tháng) và hai màn LÀM (nhập số, tra dữ liệu): đọc và nhập chung một chỗ
 * thì mở ra phải chọn mình đang định làm gì.
 *
 * Nhóm nào KHÔNG còn mục nào người xem được phép thấy thì biến mất khỏi menu (`AppLayout`), nên một
 * nhóm chuyên môn như «Producer» không hiện ra với người không làm nhạc.
 */
export const NAVIGATION: NavigationGroup[] = [
  {
    key: 'channels',
    label: 'Kênh & Nội dung',
    icon: <PlaySquareOutlined />,
    hue: 'teal',
    items: [
      { path: '/seo', label: 'SEO kênh', icon: <BarChartOutlined /> },
      { path: '/music', label: 'Kho nhạc', icon: <CustomerServiceOutlined /> },
      {
        path: '/cid',
        label: 'Quản lý CID',
        icon: <IdcardOutlined />,
        visible: (p) => p.canAccessCid,
      },
      /**
       * HẸN GIỜ CÔNG KHAI VIDEO (06/10/2026, chủ dự án: «mở video công khai lúc 5h sáng») trên kênh đã nối
       * YouTube — sửa trạng thái video của kênh công ty nên chỉ Giám đốc; máy chủ cũng chặn.
       */
      {
        path: '/video-publish',
        label: 'Đăng video',
        icon: <CalendarOutlined />,
        visible: (p) => p.canConfigure,
      },
      /**
       * TẢI VIDEO (24/10/2026 Dựng lại video → 28/10/2026 chỉ còn tải, chủ dự án: «chỉ cần chức năng tải video về
       * máy»): việc nặng chạy trên máy công ty qua cầu nối GPM, nên mở cho cùng nhóm người dùng Quản lý GPM / Vận
       * hành kênh (Giám đốc và người mở được Hệ thống mail); link kênh máy chủ chỉ cho Giám đốc.
       */
      {
        path: '/video-download',
        label: 'Tải video',
        icon: <DownloadOutlined />,
        visible: (p) => p.canConfigure || p.canAccessVault,
      },
    ],
  },
  /**
   * PRODUCER — khu riêng của bộ phận làm nhạc (chốt 23/09/2026). Trước đó «Sản xuất nhạc» nằm lẫn
   * trong «Kênh & Nội dung», nơi mọi người làm SEO kênh; tách ra để Producer mở app là thấy đúng
   * phần việc của mình, và để chỗ này còn lớn lên được (KPI, kho bài hát, sau này thêm nữa).
   * «Kho nhạc» KHÔNG chuyển sang đây: đó là kho dùng chung cho mọi nhóm, không phải việc của riêng
   * Producer.
   */
  {
    key: 'producer',
    label: 'Producer',
    icon: <SoundOutlined />,
    hue: 'green',
    items: [
      {
        path: '/production',
        label: 'Tổng hợp sản xuất nhạc',
        icon: <SlidersOutlined />,
        visible: (p) => p.canAccessProduction,
      },
      {
        path: '/production/kpi',
        label: 'Tiến độ hằng ngày',
        icon: <ProfileOutlined />,
        // Producer là Nhân viên: chỉ thấy mục này, kho bài hát vẫn đóng với họ.
        visible: (p) => p.canAccessProduction || p.canAccessProducerKpi,
      },
      {
        path: '/production/kpi/summary',
        label: 'Tổng hợp tuần & tháng',
        icon: <BarChartOutlined />,
        visible: (p) => p.canAccessProduction || p.canAccessProducerKpi,
      },
      {
        path: '/production/done',
        label: 'Dự án hoàn thành',
        icon: <CheckCircleOutlined />,
        visible: (p) => p.canAccessProduction,
      },
    ],
  },
  {
    key: 'revenue',
    label: 'Doanh thu',
    icon: <FundOutlined />,
    hue: 'amber',
    items: [
      { path: '/revenue/reports/day', label: 'Báo cáo ngày', icon: <FileSearchOutlined /> },
      { path: '/revenue/reports/week', label: 'Báo cáo tuần', icon: <FileSearchOutlined /> },
      { path: '/revenue/reports/month', label: 'Báo cáo tháng', icon: <FileSearchOutlined /> },
      { path: '/channels', label: 'Thông tin kênh', icon: <YoutubeOutlined /> },
    ],
  },
  {
    key: 'work',
    label: 'Công việc',
    icon: <CheckSquareOutlined />,
    hue: 'violet',
    items: [
      // Chấm công, báo cáo công việc, phiếu đề xuất & đơn: lên đầu menu (`DAILY`, 05/10/2026).
      { path: '/kpi', label: 'KPI', icon: <ProfileOutlined /> },
      { path: '/projects', label: 'Dự án', icon: <ProjectOutlined /> },
      /**
       * Lưới NGƯỜI × NGÀY (25/09/2026): màn cá nhân chỉ mở được một ngày một lúc, còn Tổng hợp
       * tháng thì dồn cả tháng vào một con số — chủ dự án cần nhìn bao quát mà không phải đi tìm
       * từng người.
       */
      { path: '/work-reports/days', label: 'Tổng hợp theo ngày', icon: <FileSearchOutlined /> },
      { path: '/meetings', label: 'Họp tuần', icon: <TeamOutlined /> },
    ],
  },
  /**
   * THUMBNAIL thành NHÓM RIÊNG (chốt 26/09/2026) — ngang hàng Doanh thu, Producer… chứ không nằm
   * lẫn trong Phân tích: bộ phận Thumbnail làm cả ngày trong đây, còn Phân tích là chỗ đọc số.
   */
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    icon: <PictureOutlined />,
    hue: 'orange',
    items: [
      {
        path: '/thumbnail-library',
        label: 'Upload Kho Thumbnail hằng ngày',
        icon: <FolderOpenOutlined />,
      },
      { path: '/thumbnail-summary', label: 'Tổng hợp tuần & tháng', icon: <BarChartOutlined /> },
      /**
       * TẢI THUMBNAIL ĐỐI THỦ (03/10/2026): dán link kênh, tích video, tải ảnh thành một file ZIP — thay
       * cho việc lưu từng ảnh bằng tay của bảng «Update thumbnail đối thủ» đã bỏ cùng ngày.
       */
      { path: '/thumbnail-download', label: 'Tải thumbnail đối thủ', icon: <DownloadOutlined /> },
      /**
       * LÀM THUMBNAIL (06/10/2026): phủ lại thumbnail đối thủ hoặc tự dựng (nền, nhân vật PNG, chữ), xuất JPG
       * 4K 16:9 ngay trong trình duyệt; bản thiết kế lưu chung cho cả công ty.
       */
      { path: '/thumbnail-maker', label: 'Làm thumbnail', icon: <BrushOutlined /> },
      /**
       * THUMB VIDEO NGẮN (10/10/2026): 3 mẫu chữ cố định theo ảnh mẫu của chủ dự án, chỉ nhập tên bài · ca sĩ ·
       * nhạc sĩ, nhân vật PNG bốc ngẫu nhiên, nền vẽ sẵn theo mẫu — xuất JPG ngay.
       */
      { path: '/thumbnail-quick', label: 'Thumb video ngắn', icon: <FontSizeOutlined /> },
    ],
  },
  {
    key: 'analysis',
    label: 'Phân tích',
    icon: <RadarChartOutlined />,
    hue: 'blue',
    items: [
      { path: '/market', label: 'Thị trường', icon: <LineChartOutlined /> },
      {
        path: '/competitors',
        label: 'Nghiên cứu đối thủ',
        icon: <SearchOutlined />,
        visible: (p) => p.canUseCompetitorResearch,
      },
      {
        /**
         * PHÂN TÍCH KÊNH VĂN PHÒNG (06/10/2026): kênh CÔNG TY đo bằng thước đo của mục đối thủ, gom theo văn
         * phòng → phòng → nhóm → người phụ trách. Ai thấy kênh thì xem được số; bấm «Phân tích» cần quyền
         * Nghiên cứu đối thủ (máy chủ chặn).
         */
        path: '/office-research',
        label: 'Phân tích kênh văn phòng',
        icon: <RadarChartOutlined />,
      },
    ],
  },
  {
    key: 'admin',
    label: 'Quản trị',
    icon: <ControlOutlined />,
    hue: 'rose',
    items: [
      {
        path: '/vault',
        // Đổi tên «Lưu trữ» → «Hệ thống mail» (11/10/2026, chủ dự án: «lưu trữ đổi tên sang hệ thống mail»).
        label: 'Hệ thống mail',
        icon: <LockOutlined />,
        visible: (p) => p.canAccessVault,
      },
      /**
       * QUẢN LÝ GPM (11/10/2026): điều khiển GPM Login trên máy công ty từ xa qua cầu nối — cùng nhóm người mở được
       * Hệ thống mail (profile GPM đăng nhập sẵn Gmail kênh).
       */
      {
        path: '/gpm',
        label: 'Quản lý GPM',
        icon: <DesktopOutlined />,
        visible: (p) => p.canAccessVault,
      },
      /**
       * VẬN HÀNH KÊNH (12/10/2026): bảng tải lên · SEO · công khai video cho từng kênh, liên kết với GPM. Đăng nhập làm
       * tay (profile phải mở sẵn trong Quản lý GPM) — màn này chỉ lo tải video và quản lý nội dung.
       */
      {
        path: '/channel-ops',
        label: 'Vận hành kênh',
        icon: <PlaySquareOutlined />,
        visible: (p) => p.canAccessVault,
      },
      { path: '/org-chart', label: 'Sơ đồ bộ máy công ty', icon: <ApartmentOutlined /> },
      {
        path: '/profiles',
        label: 'Hồ sơ nhân sự',
        icon: <FolderOpenOutlined />,
        visible: (p) => p.canAccessProfiles,
      },
      {
        path: '/activity',
        label: 'Lịch sử truy cập',
        icon: <HistoryOutlined />,
        visible: (p) => p.seesOthers,
      },
      { path: '/settings', label: 'Cài đặt', icon: <SettingOutlined /> },
    ],
  },
];

export function isVisible(item: NavigationItem, permissions: EffectivePermissions): boolean {
  return item.visible ? item.visible(permissions) : true;
}

/** Trang có thật nhưng KHÔNG nằm trong menu (mở từ chỗ khác) — vẫn cần tên cho thanh tiêu đề. */
const OFF_MENU_TITLES: Record<string, string> = {
  '/account': 'Tài khoản & mật khẩu',
  /** Nhật ký công việc còn mở được từ màn đầu tiên và tab trong KPI. */
  '/worklog': 'Nhật ký công việc',
};

export const APP_NAME = 'SUN MUSIC';

/** Tên màn hình đang mở — nguồn DUY NHẤT cho thanh tiêu đề và tiêu đề thẻ trình duyệt. */
export function pageTitle(pathname: string): string {
  return findNavigationItem(pathname)?.label ?? OFF_MENU_TITLES[pathname] ?? APP_NAME;
}

export function findNavigationItem(pathname: string): NavigationItem | undefined {
  if (pathname === HOME.path) return HOME;
  return [...DAILY, ...NAVIGATION.flatMap((group) => group.items)]
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

/** Tông màu của mục đang mở — theo nhóm chứa nó; màn đầu tiên và trang ngoài mục lục theo màu thương hiệu. */
export function hueForPath(pathname: string): AccentHue {
  const item = findNavigationItem(pathname);
  if (!item || item === HOME) return HOME_HUE;
  return hueOfItem(item);
}

/** Tông màu của một mục trong mục lục: màu riêng của mục rời, không thì màu của nhóm. */
export function hueOfItem(item: NavigationItem): AccentHue {
  return item.hue ?? NAVIGATION.find((group) => group.items.includes(item))?.hue ?? HOME_HUE;
}
